import {createEnv} from "@t3-oss/env-core";
import {z} from "zod";

/**
 * Centralized environment configuration.
 *
 * All `VITE_*` variables should be defined here and consumed via `env.VAR_NAME`.
 * Never access `import.meta.env.VITE_*` directly — this module provides
 * validation, type coercion, and a single source of truth.
 *
 * | Variable                 | Type    | Default | Purpose                                      |
 * |--------------------------|---------|---------|----------------------------------------------|
 * | VITE_MOCK                | boolean | false   | Enable MSW mock mode (dev/test)              |
 * | VITE_MOCK_SEED           | boolean | true    | Seed demo data (false = E2E empty state)     |
 * | VITE_CACHE_VERSION       | string  | "1"     | Query cache buster; bump on breaking changes |
 * | VITE_AI_RUNTIME_URL      | string  | —       | AI assistant runtime endpoint                |
 * | VITE_COPILOTKIT_DEVTOOLS | string  | —       | CopilotKit dev console visibility            |
 *
 * Vite built-ins (`DEV`, `PROD`, `MODE`) are fine to access directly.
 * Node-side vars (`CI`, `TEST_TAG`) belong in `playwright.config.ts`.
 */
export const env = createEnv({
  /*
   * Server-side Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {},

  /*
   * Client-side Environment variables.
   * Due to how Vite works, we must prefix these with VITE_
   */
  clientPrefix: "VITE_",
  client: {
    VITE_AI_RUNTIME_URL: z.string().optional(),
    VITE_CACHE_VERSION: z.string().optional(),
    VITE_COPILOTKIT_DEVTOOLS: z.enum(["true", "false"]).optional(),
    VITE_MOCK: z
      .string()
      // only allow "true" or "false"
      .refine((s) => s === "true" || s === "false")
      // transform to boolean
      .transform((s) => s === "true")
      .optional(),
    VITE_MOCK_SEED: z
      .string()
      .refine((s) => s === "true" || s === "false")
      .transform((s) => s === "true")
      .optional(),
  },

  /*
   * Due to how Vite works, we need to manually destructure the
   * client-side environment variables from `import.meta.env`.
   */
  runtimeEnv: import.meta.env,

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * This is especially useful for Docker builds.
   */
  skipValidation: !!import.meta.env.SKIP_ENV_VALIDATION,

  /**
   * Makes it so that empty strings are treated as undefined.
   * `SOME_VAR: z.string()` and `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
